/**
 * HumanTaskInspector — Phase 6a-5.
 *
 * Edits a `human_task` node — the form-driven human-in-the-loop primitive
 * that surfaces in the Approval Center with a custom form schema. This
 * is the most complex of the Phase 6a inspectors because it embeds a
 * mini form-schema builder (text / select / textarea fields with
 * required flags + per-field options for selects).
 *
 * Config shape (source: soarNodes.js:2847):
 *
 *   approvers      string         comma-list or @group (optional)
 *   urgency        enum           low | normal | high | critical
 *   riskLevel      enum           low | medium | high | critical
 *   approvalType   enum           single | dual
 *   timeout        number         hours; auto-reject when elapsed
 *   formSchema     object         { fields: [{ name, type, label,
 *                                              required, options? }] }
 *
 * Builder design:
 *   - Reads cfg.formSchema. If the persisted value is a *string* (raw
 *     JSON), we attempt JSON.parse so the builder gets a structured
 *     view. If parsing fails, the builder shows a raw-JSON fallback
 *     editor so the operator can fix syntax without losing data.
 *   - All writes produce a structured object (not a string); the BE
 *     accepts either and normalizes server-side per CanvasPageV3.jsx
 *     §11865 precedent.
 *
 * Spread-preservation contract per useCanvas:1098.
 */

import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Trash2, ArrowUp, ArrowDown, ExternalLink } from 'lucide-react';

const URGENCY = [
  { value: 'low',      label: 'Low' },
  { value: 'normal',   label: 'Normal' },
  { value: 'high',     label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const RISK = [
  { value: 'low',      label: 'Low' },
  { value: 'medium',   label: 'Medium' },
  { value: 'high',     label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const APPROVAL_TYPE = [
  { value: 'single', label: 'Single approver' },
  { value: 'dual',   label: 'Dual approval (both must approve)' },
];

const FIELD_TYPES = [
  { value: 'text',     label: 'Text — single line' },
  { value: 'textarea', label: 'Textarea — multi-line' },
  { value: 'select',   label: 'Select — dropdown options' },
];

export default function HumanTaskInspector({ node, onUpdate, readOnly = false }) {
  /* RC-152c-1 — Read the current workflow id from the URL so the
   * "Design form ↗" button can deep-link the standalone designer
   * to THIS form_task. Same source the canvas page reads from
   * (CanvasPageV3.jsx parses `?workflowId=` directly) — using the
   * URL avoids prop-drilling the workflow id through every
   * Inspector consumer. When wid is absent the button disables
   * with a tooltip explaining why (the only path that reaches an
   * Inspector without a saved workflow is the brand-new
   * canvas-from-template flow which has no id yet). */
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const workflowIdFromUrl = searchParams.get('workflowId');

  const cfg = node.config || {};
  const approvers    = cfg.approvers || '';
  const urgency      = cfg.urgency || 'normal';
  const riskLevel    = cfg.riskLevel || 'medium';
  const approvalType = cfg.approvalType || 'single';
  const timeout      = cfg.timeout ?? '';

  // Normalize cfg.formSchema → { fields: [...] }. Tolerate raw-JSON strings
  // (older workflows or hand-edited cells) by attempting to parse once.
  const { schemaObj, schemaParseError } = useMemo(() => {
    const raw = cfg.formSchema;
    if (raw == null || raw === '') return { schemaObj: { fields: [] }, schemaParseError: null };
    if (typeof raw === 'object') {
      return {
        schemaObj: { fields: Array.isArray(raw.fields) ? raw.fields : [] },
        schemaParseError: null,
      };
    }
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return {
          schemaObj: { fields: Array.isArray(parsed?.fields) ? parsed.fields : [] },
          schemaParseError: null,
        };
      } catch (err) {
        return { schemaObj: { fields: [] }, schemaParseError: err.message };
      }
    }
    return { schemaObj: { fields: [] }, schemaParseError: null };
  }, [cfg.formSchema]);

  const fields = schemaObj.fields;

  const setField = (key, value) => {
    if (readOnly || typeof onUpdate !== 'function') return;
    onUpdate(node.id, { config: { ...cfg, [key]: value } });
  };

  const setFields = (nextFields) => {
    if (readOnly || typeof onUpdate !== 'function') return;
    onUpdate(node.id, {
      config: { ...cfg, formSchema: { ...schemaObj, fields: nextFields } },
    });
  };

  const addField = () => {
    if (readOnly) return;
    const baseName = `field_${fields.length + 1}`;
    setFields([
      ...fields,
      { name: baseName, type: 'text', label: '', required: false },
    ]);
  };
  const removeField = (i) => {
    if (readOnly) return;
    setFields(fields.filter((_, idx) => idx !== i));
  };
  const updateField = (i, key, value) => {
    if (readOnly) return;
    const next = fields.map((f, idx) => (idx === i ? { ...f, [key]: value } : f));
    setFields(next);
  };
  const moveField = (i, dir) => {
    if (readOnly) return;
    const j = i + dir;
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[i], next[j]] = [next[j], next[i]];
    setFields(next);
  };

  // For schema-parse errors, fall back to a raw textarea so the operator
  // can repair invalid JSON without the builder erasing their work.
  if (schemaParseError) {
    return (
      <RawFormSchemaFallback
        cfg={cfg}
        onUpdate={onUpdate}
        readOnly={readOnly}
        node={node}
        error={schemaParseError}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <FieldGroup
        label="Approvers"
        hint="Optional. Comma list of emails or @group. If empty, falls back to the org/tenant approval policy."
      >
        <input
          type="text"
          value={approvers}
          onChange={(e) => setField('approvers', e.target.value)}
          placeholder="user@company.com, @security-team"
          disabled={readOnly}
          autoComplete="off"
          spellCheck={false}
          style={inputStyle}
        />
      </FieldGroup>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FieldGroup label="Urgency">
          <select
            value={URGENCY.find((u) => u.value === urgency)?.value || 'normal'}
            onChange={(e) => setField('urgency', e.target.value)}
            disabled={readOnly}
            style={selectStyle}
          >
            {URGENCY.map((u) => (<option key={u.value} value={u.value}>{u.label}</option>))}
          </select>
        </FieldGroup>

        <FieldGroup label="Risk Level">
          <select
            value={RISK.find((r) => r.value === riskLevel)?.value || 'medium'}
            onChange={(e) => setField('riskLevel', e.target.value)}
            disabled={readOnly}
            style={selectStyle}
          >
            {RISK.map((r) => (<option key={r.value} value={r.value}>{r.label}</option>))}
          </select>
        </FieldGroup>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
        <FieldGroup label="Approval Mode">
          <select
            value={APPROVAL_TYPE.find((a) => a.value === approvalType)?.value || 'single'}
            onChange={(e) => setField('approvalType', e.target.value)}
            disabled={readOnly}
            style={selectStyle}
          >
            {APPROVAL_TYPE.map((a) => (<option key={a.value} value={a.value}>{a.label}</option>))}
          </select>
        </FieldGroup>

        <FieldGroup label="Timeout (h)" hint="Auto-reject after.">
          <input
            type="number"
            min="0"
            step="1"
            value={timeout}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '') return setField('timeout', undefined);
              const n = Number(v);
              if (Number.isFinite(n)) setField('timeout', n);
            }}
            placeholder="24"
            disabled={readOnly}
            style={inputStyle}
          />
        </FieldGroup>
      </div>

      {/* ── Form schema builder ─────────────────────────────────────── */}
      <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 8,
        }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>
            Form Schema
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {/* RC-152c-1 — "Design form" button. Opens the standalone
                Form Designer scaffold at /forms/design with the current
                workflow id + this node's id in the URL. Read-only
                summary in c-1; field palette + drag/drop + per-field
                inspector light up in c-2.
                Disabled when (a) the inspector itself is read-only OR
                (b) the URL has no workflowId (brand-new unsaved canvas
                — there's nothing to deep-link to until Save lands an
                id). Tooltip in both disable cases explains why so the
                operator isn't left guessing. */}
            <button
              type="button"
              onClick={() => {
                if (!workflowIdFromUrl || !node?.id) return;
                navigate(`/forms/design?wid=${encodeURIComponent(workflowIdFromUrl)}&nid=${encodeURIComponent(node.id)}`);
              }}
              disabled={readOnly || !workflowIdFromUrl || !node?.id}
              style={{
                ...addBtnStyle,
                opacity: (readOnly || !workflowIdFromUrl || !node?.id) ? 0.5 : 1,
              }}
              title={
                readOnly
                  ? 'Inspector is read-only — exit read-only mode to edit the form'
                  : (!workflowIdFromUrl
                      ? 'Save this workflow first — Form Designer opens for a saved node'
                      : 'Open the Form Designer for this form_task')
              }
            >
              <ExternalLink size={11} aria-hidden="true" /> Design form
            </button>
            <button
              type="button"
              onClick={addField}
              disabled={readOnly}
              style={addBtnStyle}
              title="Add a new form field inline"
            >
              <Plus size={11} aria-hidden="true" /> Field
            </button>
          </div>
        </div>
        <span style={{ fontSize: 10, color: '#64748b', lineHeight: 1.4 }}>
          Fields appear in the Approval Center form. The operator's answers
          flow downstream as <code style={codeChip}>$&#123;node.fieldName&#125;</code>.
        </span>

        {fields.length === 0 ? (
          <div style={emptyRow}>
            No form fields yet. Click <strong>Field</strong> to add the first one,
            or leave blank to auto-synthesize a form from upstream output.
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {fields.map((f, i) => (
              <FormFieldRow
                key={i}
                field={f}
                index={i}
                total={fields.length}
                onChange={(key, value) => updateField(i, key, value)}
                onMoveUp={() => moveField(i, -1)}
                onMoveDown={() => moveField(i, 1)}
                onRemove={() => removeField(i)}
                readOnly={readOnly}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/* ───── individual form-field editor row ──────────────────────────── */
function FormFieldRow({ field, index, total, onChange, onMoveUp, onMoveDown, onRemove, readOnly }) {
  const type = field.type || 'text';
  const opts = Array.isArray(field.options) ? field.options : [];

  const setOptions = (next) => onChange('options', next);
  const addOption = () => setOptions([...opts, { value: '', label: '' }]);
  const removeOption = (i) => setOptions(opts.filter((_, idx) => idx !== i));
  const updateOption = (i, key, value) => {
    setOptions(opts.map((o, idx) => (idx === i ? { ...o, [key]: value } : o)));
  };

  return (
    <li style={{
      padding: 10,
      background: '#f8fafc',
      border: '0.5px solid #e2e8f0',
      borderRadius: 6,
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      {/* Row header — name + move/remove cluster */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input
          type="text"
          value={field.name || ''}
          onChange={(e) => onChange('name', e.target.value)}
          placeholder="field_name"
          disabled={readOnly}
          autoComplete="off"
          spellCheck={false}
          style={{ ...inputStyle, flex: 1, fontFamily: 'SFMono-Regular, Menlo, monospace', fontSize: 11 }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <button type="button" onClick={onMoveUp} disabled={readOnly || index === 0}
            title="Move up" style={iconBtnStyle}>
            <ArrowUp size={11} aria-hidden="true" />
          </button>
          <button type="button" onClick={onMoveDown} disabled={readOnly || index === total - 1}
            title="Move down" style={iconBtnStyle}>
            <ArrowDown size={11} aria-hidden="true" />
          </button>
          <button type="button" onClick={onRemove} disabled={readOnly}
            title="Remove field" style={{ ...iconBtnStyle, color: '#b91c1c' }}>
            <Trash2 size={11} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Type + Required */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr auto', gap: 8, alignItems: 'center' }}>
        <select
          value={type}
          onChange={(e) => onChange('type', e.target.value)}
          disabled={readOnly}
          style={selectStyle}
        >
          {FIELD_TYPES.map((t) => (<option key={t.value} value={t.value}>{t.label}</option>))}
        </select>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#0f172a' }}>
          <input
            type="checkbox"
            checked={field.required === true}
            onChange={(e) => onChange('required', e.target.checked)}
            disabled={readOnly}
          />
          Required
        </label>
      </div>

      {/* Label */}
      <input
        type="text"
        value={field.label || ''}
        onChange={(e) => onChange('label', e.target.value)}
        placeholder="Display label (what the operator sees)"
        disabled={readOnly}
        style={inputStyle}
      />

      {/* Per-select options sub-editor */}
      {type === 'select' && (
        <div style={{
          padding: 8,
          background: '#ffffff',
          border: '0.5px solid #e2e8f0',
          borderRadius: 4,
          display: 'flex', flexDirection: 'column', gap: 6,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#475569',
                           textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Options
            </span>
            <button type="button" onClick={addOption} disabled={readOnly}
                    style={addBtnStyle} title="Add an option">
              <Plus size={10} aria-hidden="true" /> Option
            </button>
          </div>
          {opts.length === 0 && (
            <span style={{ fontSize: 10, color: '#94a3b8' }}>
              No options yet — selects need at least one.
            </span>
          )}
          {opts.map((o, oi) => (
            <div key={oi} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input
                type="text"
                value={o.value || ''}
                onChange={(e) => updateOption(oi, 'value', e.target.value)}
                placeholder="value"
                disabled={readOnly}
                style={{ ...inputStyle, flex: 1, fontFamily: 'SFMono-Regular, Menlo, monospace', fontSize: 11 }}
              />
              <input
                type="text"
                value={o.label || ''}
                onChange={(e) => updateOption(oi, 'label', e.target.value)}
                placeholder="label"
                disabled={readOnly}
                style={{ ...inputStyle, flex: 1 }}
              />
              <button type="button" onClick={() => removeOption(oi)} disabled={readOnly}
                      title="Remove option" style={{ ...iconBtnStyle, color: '#b91c1c' }}>
                <Trash2 size={10} aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      )}
    </li>
  );
}

/* ───── raw-JSON fallback when the persisted schema fails to parse ── */
function RawFormSchemaFallback({ cfg, onUpdate, readOnly, node, error }) {
  // RC-152a — same fix as the v3 sibling (pretty-stringify the
  // object-shaped schema instead of returning '' which surfaces
  // "[object Object]" or stale-DOM garbage). See v3 inspector
  // for the full rationale.
  let raw;
  if (typeof cfg.formSchema === 'string') {
    raw = cfg.formSchema;
  } else if (cfg.formSchema != null) {
    try { raw = JSON.stringify(cfg.formSchema, null, 2); }
    catch { raw = ''; }
  } else {
    raw = '';
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{
        padding: 10,
        background: '#fef2f2',
        border: '0.5px solid #fecaca',
        borderRadius: 6,
        fontSize: 11, color: '#7f1d1d', lineHeight: 1.5,
      }}>
        Form schema JSON could not be parsed:&nbsp;
        <code style={{ ...codeChip, background: '#fee2e2', color: '#7f1d1d' }}>{error}</code>.
        Fix the JSON below — once it parses, the field builder will re-appear.
      </div>
      <FieldGroup label="Form Schema (raw JSON)">
        <textarea
          value={raw}
          onChange={(e) => {
            if (readOnly || typeof onUpdate !== 'function') return;
            onUpdate(node.id, { config: { ...cfg, formSchema: e.target.value } });
          }}
          disabled={readOnly}
          rows={10}
          spellCheck={false}
          style={{
            ...inputStyle,
            fontFamily: 'SFMono-Regular, Menlo, monospace',
            fontSize: 11,
            lineHeight: 1.4,
            resize: 'vertical',
          }}
        />
      </FieldGroup>
    </div>
  );
}

/* ───── shared field UI ───────────────────────────────────────────── */

function FieldGroup({ label, hint, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>{label}</span>
      {children}
      {hint && (
        <span style={{ fontSize: 10, color: '#64748b' }}>{hint}</span>
      )}
    </label>
  );
}

const inputStyle = {
  width: '100%',
  padding: '6px 8px',
  fontSize: 12,
  color: '#0f172a',
  background: '#ffffff',
  border: '0.5px solid #cbd5e1',
  borderRadius: 4,
  outline: 'none',
  fontFamily: 'inherit',
};

const selectStyle = { ...inputStyle, appearance: 'auto' };

const addBtnStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '3px 8px',
  fontSize: 11, fontWeight: 500,
  color: '#0f172a',
  background: '#ffffff',
  border: '0.5px solid #cbd5e1',
  borderRadius: 4,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const iconBtnStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 22, height: 22, padding: 0,
  background: 'transparent', border: '0.5px solid transparent',
  borderRadius: 4, color: '#475569', cursor: 'pointer',
};

const emptyRow = {
  padding: '10px 12px',
  background: '#f8fafc',
  border: '0.5px dashed #cbd5e1',
  borderRadius: 6,
  fontSize: 11, color: '#64748b',
  textAlign: 'center',
  lineHeight: 1.5,
};

const codeChip = {
  padding: '0 4px',
  borderRadius: 3,
  background: '#f1f5f9',
  color: '#475569',
  fontFamily: 'SFMono-Regular, Menlo, monospace',
  fontSize: 10,
};
